import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie } from 'sma.common/utils';
import STRINGS from '../Authentication/constants';
import MFAErrorMessage from '../Authentication/components/MFAErrorMessage';
import { clearCooldownTimer, startCooldownTimer } from '../Authentication/utils/cooldown';

const RESEND_COOLDOWN_SEC = 120;

const RecoveryEmailChallengeModal = (props) => {
	const { open, handleClose, onSuccess, newMethod, onVerified } = props;

	// State
	const [code, setCode] = useState('');
	const [error, setError] = useState('');
	const [sendingCode, setSendingCode] = useState(false);
	const [verifying, setVerifying] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [maskedEmail, setMaskedEmail] = useState('');
	const [fullEmail, setFullEmail] = useState('');
	const [isLockedOut, setIsLockedOut] = useState(false);
	const timerRef = useRef(null);
	const cooldownEndAtRef = useRef(0);

	// Initial Setup - send code when modal opens
	useEffect(() => {
		if (open) {
			setCode('');
			setError('');
			setIsLockedOut(false);
			sendRecoveryEmailCode();
		}
	}, [open]);

	useEffect(() => {
		return () => {
			clearCooldownTimer(timerRef);
		};
	}, []);

	// Clear cooldown timer when locked out
	useEffect(() => {
		if (isLockedOut) {
			clearCooldownTimer(timerRef);
			setCooldown(0);
		}
	}, [isLockedOut]);

	const startCooldown = (remaining) => {
		startCooldownTimer({
			remaining,
			defaultSeconds: RESEND_COOLDOWN_SEC,
			setCooldown,
			timerRef,
			endAtRef: cooldownEndAtRef,
		});
	};

	const sendRecoveryEmailCode = async () => {
		setSendingCode(true);
		setError('');
		try {
			console.log('[RecoveryEmailChallenge] Sending code...');
			const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action: 'send_challenge_code',
				}),
			});
			const data = await response.json();
			console.log('[RecoveryEmailChallenge] Send response:', data);
			if (data.debug_code) {
				console.log('[RecoveryEmailChallenge] DEBUG - Code sent to email:', data.debug_code);
				console.log('[RecoveryEmailChallenge] DEBUG - Please enter this code:', data.debug_code);
			}
			
			// Handle cooldown case - show code_already_pending message
			if (data.error === 'code_already_pending' && data.time_remaining) {
				if (data.masked_email) {
					setMaskedEmail(data.masked_email);
				}
				if (data.email) {
					setFullEmail(data.email);
				}
				setError('code_already_pending');
				startCooldown(data.time_remaining);
			} else if (data.success) {
				if (data.masked_email) {
					setMaskedEmail(data.masked_email);
				}
				if (data.email) {
					setFullEmail(data.email);
				}
				if (window.track) {
					window.track('mfaRecoveryEmailChallengeStarted', {
						maskedEmail: data.masked_email,
						context: 'device_change'
					});
				}
				if (data.time_remaining) {
					startCooldown(data.time_remaining);
				} else {
					startCooldown();
				}
			} else if (data.error === 'rate_limited') {
				if (window.track) {
					window.track('mfaRecoveryEmailChallengeFailed', {
						errorType: 'rate_limited',
						context: 'device_change'
					});
				}
				setIsLockedOut(true);
				setError('lockedOutMessage'); // Use same message as ChangeRecoveryEmailModal
			} else {
				if (window.track && data.error) {
					window.track('mfaRecoveryEmailChallengeFailed', {
						errorType: data.error,
						context: 'device_change'
					});
				}
				setError(data.error || 'Failed to send code');
			}
		} catch (err) {
			setError(STRINGS.unknownError);
		} finally {
			setSendingCode(false);
		}
	};

	const handleVerifyCode = async () => {
		const trimmedCode = code.trim();
		if (trimmedCode.length !== 6) return;

		setVerifying(true);
		setError('');
		try {
			console.log('[RecoveryEmailChallenge] Verifying code:', trimmedCode);
			
			// First verify the recovery email code
			const response = await fetch(reverse('api-internal-mfa-recovery-email'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action: 'verify_challenge_code',
					code: trimmedCode,
				}),
			});
			const data = await response.json();
			console.log('[RecoveryEmailChallenge] Verify response:', data);

			if (data.success) {
				if (onVerified) {
					onVerified();
					return;
				}
				console.log('[RecoveryEmailChallenge] Recovery verified, starting device change with method:', newMethod);
				const changeResponse = await fetch(reverse('api-internal-mfa-change-device'), {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRFToken': getCookie('csrftoken'),
					},
					body: JSON.stringify({
						action: 'begin_device_change',
						method: newMethod,
						use_recovery_email: true,
					}),
				});
				const changeData = await changeResponse.json();
				console.log('[RecoveryEmailChallenge] Device change response:', changeData);

				if (changeData.success) {				if (window.track) {
					window.track('mfaRecoveryEmailChallengeCompleted', {
						newMethod: newMethod,
						context: 'device_change'
					});
				}					// Success - pass the data back to parent
				// Parent will close modal and update context
				onSuccess(changeData);

				} else {
					if (window.track && changeData.error) {
						window.track('mfaRecoveryEmailChallengeFailed', {
							errorType: changeData.error,
							context: 'device_change'
						});
					}
					setError(changeData.error || 'Failed to change device');
				}
			} else {
				// Handle lockout/rate limit errors
				if (data.error === 'rate_limited' || data.error === 'invalid_code_locked_out') {
					if (window.track) {
						window.track('mfaRecoveryEmailChallengeFailed', {
							errorType: data.error,
							context: 'device_change'
						});
					}
					setIsLockedOut(true);
					setError('lockedOutMessage'); // Use same message as ChangeRecoveryEmailModal
				} else {
					if (window.track && data.error) {
						window.track('mfaRecoveryEmailChallengeFailed', {
							errorType: data.error,
							context: 'device_change'
						});
					}
					setError(data.error || 'Invalid code');
				}
			}
		} catch (err) {
			setError(STRINGS.unknownError);
		} finally {
			setVerifying(false);
		}
	};

	const handleCancel = () => {
		if (isLockedOut) {
			window.location.href = '/mfa/locked-out/';
		} else {
			handleClose();
		}
	};

	const isValid = code.length === 6 && !verifying && !isLockedOut;

	return (
		<Modal 
			show={open} 
			onClose={handleCancel} 
			size='sm'
		>
		<Modal.Body style={{ padding: '56px 40px', backgroundColor: '#FFFFFF', borderRadius: '12px' }}>

					<Box display='flex' flexDirection='column' alignItems='center'>
					{/* Logo */}
					<Box mb={5}>
						<img
							src='/media/img/smapply/apply.svg'
							alt={STRINGS.smapplyLogoAltText}
							style={{ width: '110px', height: 'auto' }}
						/>
					</Box>

					{/* Container for Text, Input, and Button */}
					<Box
						style={{
							width: '100%',
							maxWidth: '440px',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'stretch',
						}}
					>
						{/* Header Text */}
						<Box mb={4}>
							<Typography
								variant='h4'
								style={{
									fontSize: '22px',
									fontWeight: '700',
									marginBottom: '12px',
									color: '#111827',
									textAlign: 'left',
								}}
							>
							{STRINGS.enterVerificationCode}
						</Typography>
						<Typography
							style={{
								fontSize: '14px',
								color: '#6B7280',
								lineHeight: '1.6',
								textAlign: 'left',
							}}
						>
							{STRINGS.enterVerificationCodeDescription}
							{fullEmail && (
								<>
									{' ('}
									<span style={{ fontWeight: '700' }}>{fullEmail}</span>
									{')'}
								</>
							)}
						</Typography>
					</Box>

					{/* Error Box */}
					<MFAErrorMessage error={error} mb={4} style={{ width: '100%' }}  />

					{/* Input and Buttons */}
					<Box
							display='flex'
							flexDirection='column'
							gap={4}
							pb={6}
						>
							{/* Code Input */}
							<Input
								value={code}
						onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
						placeholder={STRINGS.enterCodeProvided}
						id='recovery-challenge-code'
						type='text'
						aria-required='true'
						autoFocus
						required
						disabled={isLockedOut}
						style={{
							width: '100%',
							boxSizing: 'border-box',
							padding: '0 16px',
							fontSize: '15px',
							borderRadius: '4px',
							border: '1px solid #D1D5DB',
							height: '48px',
						}}
					/>

					{/* Resend Button */}
					<Box style={{ textAlign: 'right' }}>
						<Typography
							component='span'
							onClick={(cooldown > 0 || sendingCode || isLockedOut) ? undefined : sendRecoveryEmailCode}
							style={{
								color: (cooldown > 0 || sendingCode || isLockedOut) ? '#768086' : '#007FAA',
								cursor: (cooldown > 0 || sendingCode || isLockedOut) ? 'not-allowed' : 'pointer',
								fontSize: '15px',
								fontWeight: '500'
							}}
						>
							{sendingCode
								? STRINGS.sending
								: cooldown > 0
								? `${STRINGS.resendCode} (${cooldown}s)`
								: STRINGS.resendCode}
					</Typography>
				</Box>

				{/* Confirm Button */}
				<Button
					onClick={handleVerifyCode}
					disabled={!isValid}
					style={{
						width: '100%',
						backgroundColor: isValid ? '#98D5B0' : '#E5E7EB',
						color: isValid ? '#000000' : '#9CA3AF',
						border: 'none',
						height: '48px',
						fontSize: '16px',
						fontWeight: '500',
						borderRadius: '8px',
						cursor: isValid ? 'pointer' : 'not-allowed',
					}}
				>
					{verifying ? <ProgressCircle continuous size="sm" /> : STRINGS.confirm}
				</Button>
			</Box>
		</Box>
	</Box>
		</Modal.Body>
	</Modal>
	);
};

RecoveryEmailChallengeModal.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	onSuccess: PropTypes.func,
	newMethod: PropTypes.string,
	onVerified: PropTypes.func,
};

RecoveryEmailChallengeModal.defaultProps = {
	onSuccess: null,
	newMethod: null,
	onVerified: null,
};

export default RecoveryEmailChallengeModal;
